/**
 * FrontendAudioProcessor
 *
 * Handles ALL audio/video processing client-side:
 *  - Real-time preview: mutes video + plays sound effects during profanity
 *  - Full export: uses captureStream + Web Audio API to mix sound effects
 *    into the video and record a downloadable file with NO backend needed
 *  - Clip segment download via MediaRecorder
 */

const SOUND_EFFECTS = {
  Quack: "/audio/quack_5.mp3",
  Smack: "/audio/sharp-punch-soundbible.mp3",
  "Adult-Swim Beep": "/audio/bleeep.mp3",
};

function timeToMilliseconds(time) {
  const parts = time.split(":").map(parseFloat);
  if (parts.length === 2) {
    return parts[0] * 1000 + parts[1];
  }
  if (parts.length === 3) {
    return parts[0] * 3600000 + parts[1] * 60000 + parts[2] * 1000;
  }
  return parseFloat(time) * 1000;
}

function clipRangeToSeconds(range) {
  const [startStr, endStr] = range.split("-");
  return {
    start: timeToMilliseconds(startStr.trim()) / 1000,
    end: timeToMilliseconds(endStr.trim()) / 1000,
  };
}

function scalePitch(pitchValue) {
  const p = parseFloat(pitchValue) || 50;
  return 0.5 + (p / 100) * 1.5;
}

export default class FrontendAudioProcessor {
  constructor() {
    this.audioElements = {};
    this.isMonitoring = false;
    this.animFrameId = null;
    this.videoRef = null;
    this.clipData = null;
    this.activeSegments = new Set();
    this.originalVolume = 1;
    this.initialized = false;
    this.soundBuffers = {};
  }

  async init() {
    if (this.initialized) return;

    // Load Audio elements for real-time preview
    const loadPromises = Object.entries(SOUND_EFFECTS).map(
      ([name, url]) =>
        new Promise((resolve) => {
          const audio = new Audio(url);
          audio.preload = "auto";
          audio.addEventListener("canplaythrough", () => resolve(), {
            once: true,
          });
          audio.addEventListener("error", () => resolve(), { once: true });
          audio.load();
          this.audioElements[name] = audio;
        })
    );
    await Promise.all(loadPromises);

    // Also decode as AudioBuffers for the Web Audio export pipeline
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    for (const [name, url] of Object.entries(SOUND_EFFECTS)) {
      try {
        const resp = await fetch(url);
        const arrayBuf = await resp.arrayBuffer();
        this.soundBuffers[name] = await audioCtx.decodeAudioData(arrayBuf);
      } catch {
        // non-fatal – that effect just won't be available during export
      }
    }
    audioCtx.close().catch(() => {});

    this.initialized = true;
  }

  // ─── Real-time monitoring (preview) ───────────────────────────────

  startMonitoring(videoRef, clipArray) {
    this.isMonitoring = true;
    this.videoRef = videoRef;
    this.clipData = clipArray;
    if (videoRef.current) {
      this.originalVolume = videoRef.current.volume;
    }
    this._monitor();
  }

  updateClipData(clipArray) {
    this.clipData = clipArray;
  }

  _monitor() {
    if (!this.isMonitoring) return;

    const video = this.videoRef?.current;
    if (!video) {
      this.animFrameId = requestAnimationFrame(() => this._monitor());
      return;
    }

    if (video.paused) {
      this._stopAllEffects();
      this.animFrameId = requestAnimationFrame(() => this._monitor());
      return;
    }

    const currentTime = video.currentTime;
    let inAnySegment = false;

    if (this.clipData) {
      Object.entries(this.clipData).forEach(([index, clip]) => {
        if (!clip || !clip.useForClip || clip.musicClip) return;
        const range = clip.clipLength;
        if (!range || !range.includes("-")) return;

        const { start, end } = clipRangeToSeconds(range);

        if (currentTime >= start && currentTime <= end) {
          inAnySegment = true;
          if (!this.activeSegments.has(index)) {
            this.activeSegments.add(index);
            video.volume = 0;
            if (clip.sound !== "Silence") {
              this._playEffect(
                clip.sound,
                (parseFloat(clip.volume) || 100) / 100,
                clip.pitch
              );
            }
          }
        } else if (this.activeSegments.has(index)) {
          this.activeSegments.delete(index);
        }
      });
    }

    if (!inAnySegment && this.activeSegments.size === 0) {
      video.volume = this.originalVolume;
    }

    this.animFrameId = requestAnimationFrame(() => this._monitor());
  }

  _playEffect(soundName, volume, pitch) {
    const audio = this.audioElements[soundName];
    if (!audio) return;
    const clone = audio.cloneNode();
    clone.volume = Math.min(1, Math.max(0, volume));
    clone.playbackRate = scalePitch(pitch);
    clone.currentTime = 0;
    clone.play().catch(() => {});
    clone.addEventListener("ended", () => clone.remove());
  }

  _stopAllEffects() {
    this.activeSegments.clear();
  }

  stopMonitoring() {
    this.isMonitoring = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.activeSegments.clear();
    if (this.videoRef?.current) {
      this.videoRef.current.volume = this.originalVolume;
    }
  }

  // ─── Full frontend export ─────────────────────────────────────────
  //
  // 1. captureStream() on the <video> → raw video + audio tracks
  // 2. Pipe the raw audio through a Web Audio gain node so we can
  //    mute it during profanity ranges
  // 3. Schedule AudioBufferSource nodes for each sound effect at
  //    the correct times, connected to the same audio destination
  // 4. MediaRecorder records (video track + mixed audio track)
  // 5. Video plays at 1× from 0 → end; progress callback fires per frame
  // 6. Returns the finished Blob

  async exportWithEdits(videoElement, clipArray, audioLength, onProgress) {
    if (!videoElement) throw new Error("No video element provided");
    if (!videoElement.captureStream) {
      throw new Error(
        "Your browser does not support captureStream. Please use Chrome or Edge."
      );
    }

    // Pause any live monitoring so we don't double-process
    const wasMonitoring = this.isMonitoring;
    if (wasMonitoring) this.stopMonitoring();

    const savedTime = videoElement.currentTime;
    const savedVolume = videoElement.volume;
    const savedMuted = videoElement.muted;

    try {
      // 1. Capture the raw stream from the <video>
      const rawStream = videoElement.captureStream();
      const videoTrack = rawStream.getVideoTracks()[0];
      const rawAudioTrack = rawStream.getAudioTracks()[0];
      if (!videoTrack) throw new Error("Could not capture video track");

      // 2. Set up Web Audio mixing graph
      const audioCtx = new (window.AudioContext ||
        window.webkitAudioContext)();
      const mixDest = audioCtx.createMediaStreamDestination();

      let gainNode = null;
      if (rawAudioTrack) {
        const src = audioCtx.createMediaStreamSource(
          new MediaStream([rawAudioTrack])
        );
        gainNode = audioCtx.createGain();
        gainNode.gain.value = 1;
        src.connect(gainNode);
        gainNode.connect(mixDest);
      }

      // 3. Build profanity ranges
      const ranges = [];
      if (clipArray) {
        Object.values(clipArray).forEach((clip) => {
          if (!clip || !clip.useForClip || clip.musicClip) return;
          const range = clip.clipLength;
          if (!range || !range.includes("-")) return;
          const { start, end } = clipRangeToSeconds(range);
          ranges.push({
            start,
            end,
            sound: clip.sound,
            volume: clip.volume,
            pitch: clip.pitch,
          });
        });
      }

      // 4. Combine video track + mixed audio into one MediaStream
      const combined = new MediaStream([
        videoTrack,
        ...mixDest.stream.getAudioTracks(),
      ]);

      // 5. Create MediaRecorder
      const mimeType = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
        .find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";

      const recorder = new MediaRecorder(combined, {
        mimeType,
        videoBitsPerSecond: 5_000_000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      // Mute the element's own speaker output so the user doesn't hear
      // double audio (the audio still flows through captureStream → graph)
      videoElement.muted = true;

      // 6. Play + record
      return await new Promise((resolve, reject) => {
        recorder.onerror = (e) => reject(e.error || e);

        recorder.onstop = () => {
          audioCtx.close().catch(() => {});
          resolve(new Blob(chunks, { type: mimeType }));
        };

        const startRecording = () => {
          videoElement.removeEventListener("seeked", startRecording);
          recorder.start(500);

          const t0 = audioCtx.currentTime;

          // Schedule gain automation + sound effect buffers
          if (gainNode) {
            gainNode.gain.setValueAtTime(1, t0);

            ranges.forEach((r) => {
              // Mute the original audio during this profanity window
              gainNode.gain.setValueAtTime(0, t0 + r.start);
              gainNode.gain.setValueAtTime(1, t0 + r.end);

              // Play replacement sound effect
              if (r.sound !== "Silence" && this.soundBuffers[r.sound]) {
                const bufSrc = audioCtx.createBufferSource();
                bufSrc.buffer = this.soundBuffers[r.sound];
                bufSrc.playbackRate.value = scalePitch(r.pitch);

                const effectGain = audioCtx.createGain();
                effectGain.gain.value = Math.min(
                  1,
                  Math.max(0, (parseFloat(r.volume) || 100) / 100)
                );
                bufSrc.connect(effectGain);
                effectGain.connect(mixDest);

                bufSrc.start(t0 + r.start, 0, r.end - r.start);
              }
            });
          }

          videoElement.play().catch((err) => reject(err));

          // Progress loop
          const tick = () => {
            if (!videoElement || videoElement.paused) return;
            const pct = Math.min(1, videoElement.currentTime / audioLength);
            if (onProgress) onProgress(pct);

            if (videoElement.currentTime >= audioLength - 0.15) {
              // Tiny delay so the last frames get captured
              setTimeout(() => {
                videoElement.pause();
                if (recorder.state === "recording") recorder.stop();
              }, 250);
            } else {
              requestAnimationFrame(tick);
            }
          };
          requestAnimationFrame(tick);
        };

        videoElement.currentTime = 0;
        videoElement.addEventListener("seeked", startRecording, {
          once: true,
        });
      });
    } finally {
      // Restore the video element no matter what
      videoElement.volume = savedVolume;
      videoElement.muted = savedMuted;
      videoElement.currentTime = savedTime;

      // Re-start live monitoring if it was active before
      if (wasMonitoring && this.videoRef) {
        this.startMonitoring(this.videoRef, this.clipData);
      }
    }
  }

  // ─── Clip segment download ────────────────────────────────────────

  async downloadClip(videoElement, startTime, endTime, filename, onProgress) {
    return new Promise((resolve, reject) => {
      try {
        if (!videoElement.captureStream) {
          reject(
            new Error(
              "captureStream not supported – use Chrome or Edge."
            )
          );
          return;
        }

        const stream = videoElement.captureStream();
        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
          ? "video/webm;codecs=vp9"
          : "video/webm";
        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks = [];
        const duration = endTime - startTime;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mimeType });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename || "clip.webm";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          resolve();
        };

        recorder.onerror = (e) => reject(e);

        videoElement.currentTime = startTime;

        const onSeeked = () => {
          videoElement.removeEventListener("seeked", onSeeked);
          recorder.start(100);
          videoElement.play();

          const checkTime = () => {
            if (!videoElement || videoElement.paused) return;
            const elapsed = videoElement.currentTime - startTime;
            if (onProgress) onProgress(Math.min(1, elapsed / duration));

            if (videoElement.currentTime >= endTime) {
              recorder.stop();
              videoElement.pause();
            } else {
              requestAnimationFrame(checkTime);
            }
          };
          requestAnimationFrame(checkTime);
        };

        videoElement.addEventListener("seeked", onSeeked, { once: true });
      } catch (err) {
        reject(err);
      }
    });
  }

  destroy() {
    this.stopMonitoring();
    Object.values(this.audioElements).forEach((audio) => {
      audio.pause();
      audio.src = "";
    });
    this.audioElements = {};
    this.soundBuffers = {};
    this.initialized = false;
  }
}
